import { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { Loader2, X, ZoomIn, RotateCw } from "lucide-react";

const MAX_EDGE = 1400;

// Draws the chosen crop at print-friendly size and hands back a JPEG blob.
const toBlob = (src, area, rotation) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const scale = Math.min(1, MAX_EDGE / Math.max(area.width, area.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(area.width * scale);
      canvas.height = Math.round(area.height * scale);
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      if (rotation) {
        // Rotate around the centre of the source, then take the crop window out of it.
        const box = document.createElement("canvas");
        const rad = (rotation * Math.PI) / 180;
        const sin = Math.abs(Math.sin(rad));
        const cos = Math.abs(Math.cos(rad));
        box.width = img.width * cos + img.height * sin;
        box.height = img.width * sin + img.height * cos;
        const bctx = box.getContext("2d");
        bctx.translate(box.width / 2, box.height / 2);
        bctx.rotate(rad);
        bctx.drawImage(img, -img.width / 2, -img.height / 2);
        ctx.drawImage(box, area.x, area.y, area.width, area.height, 0, 0, canvas.width, canvas.height);
      } else {
        ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, canvas.width, canvas.height);
      }
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Could not read that image."))), "image/jpeg", 0.9);
    };
    img.onerror = () => reject(new Error("Could not read that image."));
    img.src = src;
  });

/** Drag to reposition, zoom, rotate — then hand the cropped JPEG back to the caller. */
export default function ImageCropModal({ file, aspect = 1, title = "Frame the photo", onCancel, onDone }) {
  const [src, setSrc] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setArea(null);
    setBusy(false);
    setError("");
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onComplete = useCallback((_, pixels) => setArea(pixels), []);

  const save = async () => {
    if (!area) return;
    setBusy(true);
    setError("");
    try {
      const blob = await toBlob(src, area, rotation);
      const name = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
      await onDone(new File([blob], name, { type: "image/jpeg" }));
    } catch (e) {
      setError(e.message || "Could not save that crop.");
    } finally {
      setBusy(false);
    }
  };

  if (!file) return null;
  return (
    <div className="fixed inset-0 z-[70] bg-ink/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={(e) => e.stopPropagation()} data-testid="image-crop-modal">
      <div className="bg-plaster w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-brass-light">
          <h3 className="font-display text-lg text-ink">{title}</h3>
          <button onClick={onCancel} data-testid="crop-cancel" aria-label="Close"
                  className="text-muted hover:text-ink transition-colors duration-150"><X size={16} /></button>
        </div>
        <div className="relative h-[320px] bg-ink">
          {src ? (
            <Cropper image={src} crop={crop} zoom={zoom} rotation={rotation} aspect={aspect}
                     onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete}
                     restrictPosition showGrid={false} objectFit="contain" />
          ) : null}
        </div>
        <div className="px-5 py-4 space-y-3">
          <label className="flex items-center gap-3 text-xs text-muted">
            <ZoomIn size={14} className="text-brass shrink-0" />
            <input type="range" min={1} max={4} step={0.01} value={zoom} data-testid="crop-zoom"
                   onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-brass" />
            <span className="tabular w-10 text-end">{zoom.toFixed(1)}×</span>
          </label>
          <div className="flex items-center justify-between gap-3">
            <button className="btn-ghost text-[11px] inline-flex items-center gap-1.5" data-testid="crop-rotate"
                    onClick={() => setRotation((r) => (r + 90) % 360)}>
              <RotateCw size={13} /> Rotate
            </button>
            <p className="text-[11px] text-muted flex-1">Drag the photo to reposition it.</p>
            <button className="btn-primary text-[11px] px-4 inline-flex items-center gap-2" disabled={busy || !area}
                    onClick={save} data-testid="crop-save">
              {busy ? <Loader2 size={13} className="animate-spin" /> : null} Use this photo
            </button>
          </div>
          {error ? <p className="text-danger text-xs" data-testid="crop-error">{error}</p> : null}
        </div>
      </div>
    </div>
  );
}
